import type { AttachedDoc, Blob, Class, IntegrationKind, Ref, Timestamp } from '@hcengineering/core'
import type { Person } from '@hcengineering/contact'
import type { Asset, IntlString, Metadata, Plugin } from '@hcengineering/platform'
import { plugin } from '@hcengineering/platform'
import type { IntegrationType } from '@hcengineering/setting'
import type { AnyComponent } from '@hcengineering/ui'
import type { Action, ActionCategory, ViewAction } from '@hcengineering/view'

/**
 * @public
 */
export const esignId = 'dztek-esign' as Plugin

/**
 * @public
 */
export const esignIntegrationKind = 'dztek-esign' as IntegrationKind

/**
 * @public
 */
export type SignatureRequestStatus = 'requested' | 'viewed' | 'completed' | 'declined' | 'expired'

/**
 * @public
 */
export type SignerStatus = 'requested' | 'viewed' | 'completed' | 'declined'

/**
 * @public
 */
export interface Signer {
  name: string
  email: string
  status: SignerStatus
  signedAt?: Timestamp
}

/**
 * A document sent for electronic signature, attached to the object that holds the source PDF.
 * @public
 */
export interface SignatureRequest extends AttachedDoc {
  title: string
  provider: 'docuseal'
  /** DocuSeal submission id. */
  externalId: string
  status: SignatureRequestStatus
  signers: Signer[]
  message?: string
  sentBy?: Ref<Person>
  sentAt: Timestamp
  completedAt?: Timestamp
  /** Blob of the signed PDF, stored as a new attachment of the same object. */
  signedFileRef?: Ref<Blob>
  /** Name of that attachment. */
  signedFileName?: string
}

/**
 * Name of the collection on the parent object.
 * @public
 */
export const SIGNATURE_REQUESTS_COLLECTION = 'signatureRequests'

export default plugin(esignId, {
  class: {
    SignatureRequest: '' as Ref<Class<SignatureRequest>>
  },
  component: {
    Connect: '' as AnyComponent,
    Configure: '' as AnyComponent,
    IconEsign: '' as AnyComponent,
    SendForSignature: '' as AnyComponent,
    SignatureRequestPresenter: '' as AnyComponent,
    SignatureRequests: '' as AnyComponent,
    SignButton: '' as AnyComponent,
    StatusPresenter: '' as AnyComponent
  },
  actionImpl: {
    SendForSignature: '' as ViewAction
  },
  action: {
    SendForSignature: '' as Ref<Action>
  },
  category: {
    Esign: '' as Ref<ActionCategory>
  },
  integrationType: {
    Esign: '' as Ref<IntegrationType>
  },
  metadata: {
    ServiceUrl: '' as Metadata<string>
  },
  string: {
    Esign: '' as IntlString,
    Description: '' as IntlString,
    Sign: '' as IntlString,
    SendForSignature: '' as IntlString,
    SignatureRequest: '' as IntlString,
    SignatureRequests: '' as IntlString,
    Signers: '' as IntlString,
    Status: '' as IntlString,
    Title: '' as IntlString,
    Message: '' as IntlString,
    Provider: '' as IntlString,
    ExternalId: '' as IntlString,
    SentBy: '' as IntlString,
    SentAt: '' as IntlString,
    CompletedAt: '' as IntlString,
    SignedFile: '' as IntlString,
    StatusRequested: '' as IntlString,
    StatusViewed: '' as IntlString,
    StatusCompleted: '' as IntlString,
    StatusDeclined: '' as IntlString,
    StatusExpired: '' as IntlString
  },
  icon: {
    Esign: '' as Asset,
    Sign: '' as Asset
  }
})
